/**
 * Firebase Contact Inquiries Service
 * CRUD operations for contact form submissions
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  getCountFromServer,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import type { ContactInquiry, CreateInquiryData, InquiryStatus } from '@/types/inquiry.types';

const collectionRef = collection(db, COLLECTIONS.CONTACT_INQUIRIES);

/**
 * Create a new contact inquiry (public -- called from Contact form)
 */
export const createInquiry = async (data: CreateInquiryData): Promise<string> => {
  const docRef = await addDoc(collectionRef, {
    ...data,
    status: 'new' as InquiryStatus,
    isRead: false,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
};

/**
 * Get all inquiries ordered by newest first (admin)
 */
export const getAllInquiries = async (): Promise<ContactInquiry[]> => {
  try {
    const q = query(collectionRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as ContactInquiry[];
  } catch (error) {
    console.error('Error fetching inquiries:', error);
    throw new Error('Failed to fetch inquiries');
  }
};

/**
 * Get total inquiry count (for dashboard stat card)
 */
export const getInquiriesCount = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collectionRef);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting inquiries count:', error);
    return 0;
  }
};

/**
 * Update inquiry status
 */
export const updateInquiryStatus = async (id: string, status: InquiryStatus): Promise<void> => {
  try {
    await updateDoc(doc(db, COLLECTIONS.CONTACT_INQUIRIES, id), { status });
  } catch (error) {
    console.error('Error updating inquiry status:', error);
    throw new Error('Failed to update inquiry status');
  }
};

/**
 * Mark inquiry as read
 */
export const markAsRead = async (id: string): Promise<void> => {
  try {
    await updateDoc(doc(db, COLLECTIONS.CONTACT_INQUIRIES, id), {
      isRead: true,
      status: 'read' as InquiryStatus,
    });
  } catch (error) {
    console.error('Error marking inquiry as read:', error);
  }
};

/**
 * Delete an inquiry
 */
export const deleteInquiry = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.CONTACT_INQUIRIES, id));
  } catch (error) {
    console.error('Error deleting inquiry:', error);
    throw new Error('Failed to delete inquiry');
  }
};
